import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

// ALWAYS use relative URL - same-origin requests only
const API = '/api';

const Register = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);

    if (!termsAccepted) {
      setError({ 
        message: 'Terms acceptance required', 
        code: 'AUTH_ERR_TERMS',
        suggestion: 'You must accept the Terms & Conditions to register.'
      });
      return;
    }

    if (password.length < 8) {
      setError({
        message: 'Password too short',
        code: 'AUTH_ERR_PASSWORD',
        suggestion: 'Password must be at least 8 characters long.'
      });
      return;
    }

    setLoading(true);
    
    const requestUrl = `${API}/auth/register`;
    console.log('[Register] Request URL:', requestUrl);
    console.log('[Register] API base:', API);

    try {
      const response = await axios.post(
        requestUrl,
        { name, email, password },
        { withCredentials: true }
      );
      
      console.log('[Register] Success:', response.status);
      toast.success('Account created successfully!');
      navigate('/dashboard', { state: { user: response.data } });
    } catch (err) {
      console.error('[Register] Error:', err);
      console.error('[Register] Request URL was:', requestUrl);
      
      // Enhanced error handling with detailed messages
      let errorMessage = 'Registration failed';
      let errorCode = 'AUTH_ERR_UNKNOWN';
      let suggestion = 'Please try again or contact support.';

      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        errorCode = `AUTH_ERR_${status}`;

        if (status === 400) {
          if (detail?.includes('already registered') || detail?.includes('Email already')) {
            errorMessage = 'Email already registered';
            suggestion = 'Try logging in instead or use a different email.';
            errorCode = 'AUTH_ERR_EMAIL_EXISTS';
          } else {
            errorMessage = detail || 'Invalid registration data';
            suggestion = 'Please check all fields and try again.';
          }
        } else if (status === 422) {
          errorMessage = 'Invalid email format';
          suggestion = 'Please enter a valid email address.';
        } else if (status === 429) {
          errorMessage = 'Too many registration attempts';
          suggestion = 'Please wait a few minutes before trying again.';
        } else if (status >= 500) {
          errorMessage = 'Server error';
          suggestion = 'Our servers are having issues. Please try again later.';
        } else {
          errorMessage = detail || `Error (${status})`;
        }
      } else if (err.request) {
        // Request was made but no response received
        errorCode = 'AUTH_ERR_NETWORK';
        errorMessage = 'Unable to connect to server';
        suggestion = `Request failed. URL: ${requestUrl}. Check browser console for details.`;
        console.error('[Register] No response received. Possible CORS or network issue.');
      } else {
        // Something else happened
        errorCode = 'AUTH_ERR_SETUP';
        errorMessage = err.message || 'Request setup failed';
        suggestion = 'An unexpected error occurred. Please try again.';
      }

      setError({ message: errorMessage, code: errorCode, suggestion });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 px-4">
      <Card className="w-full max-w-md" data-testid="register-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading">Create Account</CardTitle>
          <CardDescription>Start scanning ads for risk assessment</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Error Banner */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg" data-testid="register-error-banner">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">{error.message}</p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error.suggestion}</p>
                    <p className="text-xs text-red-500 dark:text-red-500 mt-1 font-mono">Code: {error.code}</p>
                  </div>
                </div>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                data-testid="register-password-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters</p>
            </div>

            {/* Terms Acceptance */}
            <div className="border rounded-md p-3 bg-muted/30">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="termsRegister"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="h-4 w-4 mt-0.5"
                  data-testid="register-terms-checkbox"
                  required
                />
                <label htmlFor="termsRegister" className="text-xs text-muted-foreground cursor-pointer">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" className="text-primary hover:underline font-semibold">
                    Terms & Conditions
                  </a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" className="text-primary hover:underline font-semibold">
                    Privacy Policy
                  </a>
                  . I understand that ScanMyAd provides AI-powered risk assessment and does not constitute legal advice.
                </label>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading || !termsAccepted} data-testid="register-submit-btn">
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignup}
            data-testid="google-signup-btn"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign up with Google
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="goto-login-link">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
